import React, { useState } from "react";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { ChevronDown, ChevronRight, Sparkles, LayoutList } from "lucide-react";

/**
 * EmailAngleTable — DASH-EMAIL-011a, cut over in DASH-EMAIL-012A to the
 * real Angle rollup (GET creative-analytics/angles). Two mock-only columns
 * are gone rather than rendered as a fake pending state (DASH-EMAIL-012A
 * Frozen Decision 10): the real CreativeAnalyticsAngleRollup has no
 * `campaign_count`/`broadcast_count` field at all — a genuine, disclosed
 * gap versus the mock (see the 012A completion report), not something this
 * component can honestly reconstruct client-side. The three separate
 * illustrative metric columns (Unique Clicks/Quote Completed/Revenue)
 * collapse into one real `channel2_metrics` column — the real rollup
 * returns a single Channel-2 availability object per Angle, not a
 * per-outcome breakdown.
 *
 * Angle rollup membership (creative_count) is computed upstream by the
 * real catalog projection service using Creative.angle_id only, never
 * Campaign.angle_id (verified directly against
 * email_marketing_catalog_projection_service.py during DASH-EMAIL-012's
 * characterization) — this component only displays what it's given.
 */
function MetricCell({ metric, testId }) {
  const isUnavailable = metric?.availability === "UNAVAILABLE";
  const display = isUnavailable ? "—" : Number(metric?.value || 0).toLocaleString();
  return (
    <TableCell className="text-right font-mono text-slate-100" data-testid={testId}>
      <span data-metric-availability={isUnavailable ? "unavailable" : "available"}>{display}</span>
    </TableCell>
  );
}

export default function EmailAngleTable({ rows, onSelectAngle }) {
  const [expandedId, setExpandedId] = useState(null);
  const data = rows || [];

  if (data.length === 0) {
    return (
      <Card className="glass border-white/10" data-testid="email-angle-table-empty">
        <CardContent className="p-8 text-center text-slate-400">
          <p className="text-lg">No Angles for this selection</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass border-white/10" data-testid="email-angle-table">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <Sparkles className="h-5 w-5 text-indigo-400" />
          Angles
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-8 text-slate-400" />
              <TableHead className="text-slate-400">Angle</TableHead>
              <TableHead className="text-right text-slate-400">Creatives</TableHead>
              <TableHead className="text-right text-slate-400">Channel-2 Outcomes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row) => {
              const isExpanded = expandedId === row.angle_id;
              return (
                <React.Fragment key={row.angle_id}>
                  <TableRow
                    className="cursor-pointer border-white/10 hover:bg-white/5"
                    data-testid={`email-angle-row-${row.angle_id}`}
                    onClick={() => setExpandedId(isExpanded ? null : row.angle_id)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-slate-400" data-testid={`email-angle-chevron-down-${row.angle_id}`} />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" data-testid={`email-angle-chevron-right-${row.angle_id}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-100">{row.name}</div>
                      <div className="text-xs text-slate-400">{row.angle_id}</div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-slate-100">{row.creative_count.toLocaleString()}</TableCell>
                    <MetricCell metric={row.channel2_metrics} testId={`email-angle-channel2-${row.angle_id}`} />
                  </TableRow>
                  {isExpanded && (
                    <TableRow className="border-white/10 hover:bg-transparent" data-testid={`email-angle-row-detail-${row.angle_id}`}>
                      <TableCell />
                      <TableCell colSpan={3}>
                        <div className="grid grid-cols-1 gap-2 py-2 text-xs text-slate-300 sm:grid-cols-3">
                          <span>
                            Status: <span className="text-slate-100">{row.status}</span>
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-fit gap-1.5 px-2 text-xs text-indigo-300 hover:text-indigo-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectAngle?.(row.angle_id);
                            }}
                            data-testid={`email-angle-view-creatives-${row.angle_id}`}
                          >
                            <LayoutList className="h-3 w-3" />
                            View Creatives for this Angle
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
